import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import Globe from "react-globe.gl";
import * as THREE from "three";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Loader2, Mic, MicOff } from "lucide-react";
import { api } from "@/lib/api";
import { logError } from "@/lib/logger";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";
import { MapboxView } from "@/components/MapboxView";

export const GlobeView = ({ active }) => {
  const wrapRef = useRef(null);
  const globeRef = useRef(null);
  const audioRef = useRef(null);
  const [size, setSize] = useState({ w: 800, h: 600 });
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [markers, setMarkers] = useState([]);
  const [reply, setReply] = useState("Pídeme un lugar y volaré hasta allí. Prueba: «KAI, llévame a Madrid».");
  const [mode, setMode] = useState("globe"); // "globe" | "map"
  const [place, setPlace] = useState(null);
  const [transitioning, setTransitioning] = useState(false);
  const [countries, setCountries] = useState({ features: [] });

  // Holographic dark-glass globe material (JARVIS-style).
  const globeMaterial = useMemo(() => new THREE.MeshPhongMaterial({
    color: new THREE.Color("#08182b"),
    emissive: new THREE.Color("#0a2e4d"),
    emissiveIntensity: 0.75,
    shininess: 1,
    transparent: true,
    opacity: 0.9,
  }), []);

  useEffect(() => {
    fetch("//unpkg.com/three-globe/example/datasets/ne_110m_admin_0_countries.geojson")
      .then((r) => r.json())
      .then((geo) => setCountries(geo))
      .catch(() => { /* keep empty; globe still renders */ });
  }, []);

  useEffect(() => {
    const update = () => {
      if (wrapRef.current) setSize({ w: wrapRef.current.clientWidth, h: wrapRef.current.clientHeight });
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [active, mode]);

  useEffect(() => {
    const g = globeRef.current;
    if (!g || mode !== "globe") return;
    const controls = g.controls();
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.5;
    g.pointOfView({ lat: 25, lng: -10, altitude: 2.5 });
  }, [size.w, mode]);

  const stopSpeaking = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
  }, []);

  const speak = useCallback(async (text) => {
    try {
      stopSpeaking();
      const { data } = await api.post("/voice/speak", { text });
      const audio = new Audio(`data:audio/mp3;base64,${data.audio}`);
      audioRef.current = audio;
      await audio.play();
    } catch (e) {
      logError("TTS error", e);
    }
  }, [stopSpeaking]);

  // Globe fly-to, then smooth transition into the detailed Mapbox map.
  const goToPlace = useCallback((p) => {
    setMarkers([{ lat: p.lat, lng: p.lng, name: p.name }]);
    const g = globeRef.current;
    if (g) {
      g.controls().autoRotate = false;
      g.pointOfView({ lat: p.lat, lng: p.lng, altitude: 0.12 }, 2600);
    }
    setTimeout(() => {
      setTransitioning(true);
      setTimeout(() => {
        setPlace(p);
        setMode("map");
        setTimeout(() => setTransitioning(false), 700);
      }, 500);
    }, 2400);
  }, []);

  const handleCommand = useCallback(async (text) => {
    const msg = (text || "").trim();
    if (!msg) return;
    setLoading(true);
    setReply("Procesando…");
    try {
      const { data } = await api.post("/assistant/command", { message: msg });
      setReply(data.reply || "Entendido.");
      speak(data.reply || "Entendido.");
      if ((data.intent === "travel" || data.intent === "search") && data.place) {
        goToPlace(data.place);
      }
    } catch (e) {
      logError("command error", e);
      setReply("Disculpa, ha habido un problema al procesar tu petición.");
    } finally {
      setLoading(false);
    }
  }, [speak, goToPlace]);

  const onVoiceResult = useCallback((t) => { setQuery(t); handleCommand(t); }, [handleCommand]);

  const { supported: voiceSupported, listening, start, stop } = useSpeechRecognition({
    lang: "es-ES",
    onResult: onVoiceResult,
  });

  useEffect(() => {
    if (!voiceSupported) return undefined;
    if (!active) stop();
    return () => stop();
  }, [active, voiceSupported, stop]);

  const toggleMic = () => {
    if (!voiceSupported) return;
    if (listening) stop();
    else start();
  };

  const backToGlobe = () => {
    stopSpeaking();
    setMode("globe");
    setPlace(null);
    setReply("De vuelta a la vista global. ¿A dónde vamos ahora?");
  };

  return (
    <div ref={wrapRef} className="flex-1 relative overflow-hidden" data-testid="globe-view">
      {mode === "globe" ? (
        <Globe
          ref={globeRef}
          width={size.w}
          height={size.h}
          backgroundColor="rgba(0,0,0,0)"
          backgroundImageUrl="//unpkg.com/three-globe/example/img/night-sky.png"
          globeMaterial={globeMaterial}
          showAtmosphere
          atmosphereColor="#22d3ee"
          atmosphereAltitude={0.3}
          hexPolygonsData={countries.features}
          hexPolygonResolution={3}
          hexPolygonMargin={0.22}
          hexPolygonColor={() => "#34f5ff"}
          hexPolygonAltitude={0.02}
          pointsData={markers}
          pointLat="lat" pointLng="lng"
          pointColor={() => "#7dffff"} pointAltitude={0.08} pointRadius={0.6}
          ringsData={markers}
          ringLat="lat" ringLng="lng"
          ringColor={() => (t) => `rgba(0,240,255,${1 - t})`}
          ringMaxRadius={5}
          ringPropagationSpeed={2.4}
          ringRepeatPeriod={700}
          labelsData={markers}
          labelLat="lat" labelLng="lng" labelText="name"
          labelSize={1.6} labelColor={() => "#a5f3fc"} labelDotRadius={0.45} labelResolution={2}
        />
      ) : (
        <MapboxView place={place} onBack={backToGlobe} />
      )}

      {/* transition flash */}
      <AnimatePresence>
        {transitioning && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 z-20 pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(0,229,255,0.25), #03060f 70%)" }}
          />
        )}
      </AnimatePresence>

      {/* command bar (hidden on map view; map has its own controls) */}
      {mode === "globe" && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-[92%] max-w-2xl z-10">
          <p data-testid="kai-reply" className="text-center font-mono text-xs text-cyan-300/90 mb-3 text-glow min-h-[16px]">
            {loading ? "Procesando…" : reply}
          </p>
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <input
                data-testid="globe-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCommand(query)}
                placeholder="KAI, llévame a Madrid…"
                className="w-full bg-black/50 border border-cyan-500/20 rounded-full pl-6 pr-14 py-4 focus:outline-none focus:ring-1 focus:ring-cyan-500/50 focus:border-cyan-500/50 backdrop-blur-md transition-all text-slate-200 placeholder:text-slate-500"
              />
              <button
                data-testid="globe-search-btn"
                onClick={() => handleCommand(query)}
                disabled={loading || !query.trim()}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-cyan-500/90 hover:bg-cyan-400 text-slate-950 flex items-center justify-center transition-all disabled:opacity-40 active:scale-95"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
            <button
              data-testid="globe-mic-btn"
              onClick={toggleMic}
              className={`w-14 h-14 rounded-full border flex items-center justify-center transition-all shrink-0 ${
                listening
                  ? "bg-cyan-500/20 text-cyan-300 border-cyan-400 shadow-[0_0_30px_rgba(34,211,238,0.6)] animate-pulse"
                  : "border-cyan-500/30 text-cyan-400 hover:border-cyan-400 hover:shadow-[0_0_15px_rgba(34,211,238,0.5)]"
              }`}
            >
              {listening ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
